import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { addBusinessDays, addDays, differenceInBusinessDays } from 'date-fns';
import { Input, Select, Button } from '@/components/ui';
import MarketTypeahead from '@/components/ui/MarketTypeahead';
import MediaFormatTypeahead from '@/components/ui/MediaFormatTypeahead';
import { LoadingSpinner } from '@/components/common';
// import InventoryRestrictionsDisplay from './InventoryRestrictionsDisplay';
import { useSalesMarkets } from '@/hooks/useSalesMarkets';
import { useMediaFormats } from '@/hooks/useMediaFormats';
import { useUsersByRole } from '@/hooks/useUsers';
import { useValidateProductId } from '@/hooks/useInventory';
import ExceptionModal, { wasExceptionModalShown, markExceptionModalShown } from './ExceptionModal';
import type { CreateLineItemData, InventoryLineItem, ShowingType } from '@/types';
import { useShowingTypes } from '@/hooks/useShowingTypes';

// Validation schema for line item form
const lineItemSchema = z.object({
  market: z.string().min(1, 'Market is required'),
  mediaType: z.string().min(1, 'Media type is required').refine(
    (val) => { const n = parseInt(val, 10); return !isNaN(n) && n > 0; },
    { message: 'Select a valid media format' }
  ),
  unitProductId: z.string().optional(),
  coverage: z.string().optional(),
  flightStartDate: z.string().min(1, 'Flight start date is required'),
  flightEndDate: z.string().min(1, 'Flight end date is required'),
  isExceptionHold: z.boolean().default(false),
  holdExpirationDate: z.string().optional(),
  serviceAeId: z.string().optional(),
  showingType: z.string().optional(),
}).refine(
  (data) => {
    // Either unitProductId or coverage must be provided
    return (data.unitProductId && data.unitProductId.trim() !== '') || 
           (data.coverage && data.coverage.trim() !== '');
  },
  {
    message: 'Either Unit/Product ID or Coverage must be provided',
    path: ['unitProductId'],
  }
).refine(
  (data) => {
    // Date range validation: flight start <= flight end
    const flightStart = new Date(data.flightStartDate);
    const flightEnd = new Date(data.flightEndDate);
    return flightStart <= flightEnd;
  },
  {
    message: 'Flight end date must be after flight start date',
    path: ['flightEndDate'],
  }
);

type LineItemFormData = z.infer<typeof lineItemSchema>;

interface LineItemFormProps {
  onSubmit: (data: CreateLineItemData) => void | Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  defaultValues?: Partial<InventoryLineItem>;
  businessCategory?: string | undefined;
}

export default function LineItemForm({
  onSubmit,
  onCancel,
  isLoading = false,
  defaultValues,
  businessCategory,
}: LineItemFormProps) {
  // Fetch sales markets from API
  const { data: salesMarkets, isLoading: marketsLoading } = useSalesMarkets();

  // Fetch media formats from API
  const { data: mediaFormats, isLoading: mediaFormatsLoading } = useMediaFormats();
  
  // Fetch Service AEs for cross-region line items
  const { data: serviceAes = [], isLoading: isLoadingServiceAes } = useUsersByRole('service_ae');

  // Fetch showing types from API
  const { data: showingTypes = [], isLoading: isLoadingShowingTypes } = useShowingTypes();

  // Convert Service AEs to dropdown options
  const serviceAeOptions = useMemo(() => {
    return serviceAes.map((ae) => ({
      value: ae.id,
      label: `${ae.firstName} ${ae.lastName} (${ae.email})`,
    }));
  }, [serviceAes]);
  // Exception modal state
  const [showExceptionModal, setShowExceptionModal] = useState(false);

  // Product/Inventory ID validation state
  const [validationId, setValidationId] = useState('');
  const [validationTriggered, setValidationTriggered] = useState(false);

  const getDefaultValues = () => {
    if (defaultValues) {
      return {
        isExceptionHold: false,
        market: defaultValues.market || '',
        mediaType: defaultValues.mediaFormatId ? String(defaultValues.mediaFormatId) : undefined,
        unitProductId: defaultValues.unitProductId || '',
        coverage: defaultValues.coverage || '',
        flightStartDate: defaultValues.flightStartDate ? defaultValues.flightStartDate.split('T')[0] : '',
        flightEndDate: defaultValues.flightEndDate ? defaultValues.flightEndDate.split('T')[0] : '',
        showingType: defaultValues.showingType || '',
      };
    }
    
    return {
      isExceptionHold: false,
      market: '',
      unitProductId: '',
      coverage: '',
      flightStartDate: '',
      flightEndDate: '',
      showingType: '',
    } as Partial<LineItemFormData>;
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LineItemFormData>({
    resolver: zodResolver(lineItemSchema),
    defaultValues: getDefaultValues(),
  });

  const market = watch('market');
  const mediaType = watch('mediaType');
  const isExceptionHold = watch('isExceptionHold');
  const flightStartDate = watch('flightStartDate');

  // Resolve media format name for expiration calculation
  const mediaFormatName = useMemo(() => {
    if (!mediaType || !mediaFormats) return '';
    const id = parseInt(mediaType, 10);
    if (isNaN(id)) return '';
    const format = mediaFormats.find(f => f.id === id);
    return format?.name?.toLowerCase() || '';
  }, [mediaType, mediaFormats]);

  // Compute default hold expiration date based on category rules
  const computedExpirationDate = useMemo(() => {
    const today = new Date();
    if (businessCategory && businessCategory.toLowerCase() === 'movies') {
      return addDays(today, 60);
    }
    // OPE-465: whole-word match, mirroring the backend predicate (isDigitalMediaType,
    // /\bdigital\b/i) so this UX preview never disagrees with the server's policy.
    if (/\bdigital\b/i.test(mediaFormatName) && flightStartDate) {
      const flight = new Date(flightStartDate);
      const gap = differenceInBusinessDays(flight, today);
      if (gap <= 14) {
        return addBusinessDays(today, 3);
      }
    }
    return addDays(today, 5);
  }, [businessCategory, mediaFormatName, flightStartDate]);

  const formattedDefaultExpiration = useMemo(() => {
    return computedExpirationDate.toISOString().split('T')[0];
  }, [computedExpirationDate]);

  // Dual-ID validation via on-blur — market is optional for cross-market lookup
  const {
    data: validationResult,
    isLoading: isValidating,
    isFetching: isValidationFetching,
  } = useValidateProductId(
    validationTriggered ? validationId : '',
    validationTriggered && market ? market : undefined
  );

  const handleProductIdBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      if (value) {
        setValidationId(value);
        setValidationTriggered(true);
      } else {
        setValidationTriggered(false);
      }
    },
    []
  );

  // Auto-populate market when validation resolves a single match (Task 6.2)
  useEffect(() => {
    if (validationResult?.valid && validationResult.matchedMarket && !market) {
      const resolved = salesMarkets?.find(
        (m) => m.market_name.toLowerCase() === validationResult.matchedMarket!.toLowerCase()
      );
      if (resolved) {
        setValue('market', resolved.market_code, { shouldValidate: true });
      }
    }
  }, [validationResult, market, salesMarkets, setValue]);

  // Handle disambiguation market selection (Task 6.3)
  const handleDisambiguationSelect = useCallback(
    (marketName: string) => {
      const resolved = salesMarkets?.find(
        (m) => m.market_name.toLowerCase() === marketName.toLowerCase()
      );
      if (resolved) {
        setValue('market', resolved.market_code, { shouldValidate: true });
        // Re-trigger validation with the selected market
        setValidationTriggered(true);
      }
    },
    [salesMarkets, setValue]
  );

  // Derive validation display state
  const showValidationLoading = validationTriggered && (isValidating || isValidationFetching);
  const showValidationSuccess = validationTriggered && validationResult && validationResult.valid;
  const showValidationError = validationTriggered && validationResult && !validationResult.valid && !validationResult.needsDisambiguation;
  const showDisambiguation = validationTriggered && validationResult?.needsDisambiguation;

  const handleFormSubmit = (data: LineItemFormData) => {
    const submitData: CreateLineItemData = {
      market: data.market,
      media_format_id: parseInt(data.mediaType, 10),
      unitProductId: data.unitProductId || undefined,
      coverage: data.coverage || undefined,
      flightStartDate: data.flightStartDate,
      flightEndDate: data.flightEndDate,
      isExceptionHold: data.isExceptionHold,
      holdExpirationDate: data.isExceptionHold && data.holdExpirationDate
        ? data.holdExpirationDate
        : undefined,
      serviceAeId: data.serviceAeId || undefined,
      showingType: (data.showingType || undefined) as ShowingType | undefined,
    };
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {businessCategory === 'Movies' && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            <strong>Movies Requirements:</strong> A minimum 60 business-day gap is required 
            between the hold expiration date and flight start date.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MarketTypeahead
          value={watch('market')}
          onChange={(code) => setValue('market', code, { shouldValidate: true })}
          markets={salesMarkets ?? []}
          isLoading={marketsLoading}
          error={errors.market?.message}
          label="Market"
        />

        <MediaFormatTypeahead
          value={watch('mediaType') || ''}
          onChange={(id) => setValue('mediaType', id, { shouldValidate: true })}
          mediaFormats={mediaFormats ?? []}
          isLoading={mediaFormatsLoading}
          error={errors.mediaType?.message}
          label="Media Type"
        />
      </div>

      {/* Display inventory restrictions when market and media type are selected */}
      {/* TODO: Rebuild inventory restrictions feature */}
      {/* {market && mediaType && (
        <InventoryRestrictionsDisplay market={market} mediaType={mediaType} />
      )} */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Unit/Product ID"
            placeholder="Enter unit or product ID"
            error={errors.unitProductId?.message}
            {...register('unitProductId', {
              onBlur: handleProductIdBlur,
            })}
          />
          {showValidationLoading && (
            <div className="mt-1 flex items-center gap-1 text-sm text-neutral-400">
              <LoadingSpinner size="sm" />
              <span>Validating...</span>
            </div>
          )}
          {showValidationSuccess && validationResult.matchSource === 'product_catalog' && (
            <p className="mt-1 text-sm text-green-600">Matched: Product Catalog</p>
          )}
          {showValidationSuccess && validationResult.matchSource === 'inventory' && (
            <p className="mt-1 text-sm text-green-600">Matched: Inventory</p>
          )}
          {showDisambiguation && validationResult.markets && (
            <div className="relative">
              <div className="absolute left-0 top-0 z-[9999] bg-white border border-neutral-200 rounded-md shadow-lg min-w-[200px]">
                <p className="px-3 py-1.5 text-xs text-neutral-500 border-b border-neutral-100">
                  Found in {validationResult.markets.length} markets:
                </p>
                <ul className="max-h-[200px] overflow-y-auto">
                  {validationResult.markets.map((mkt) => (
                    <li
                      key={mkt}
                      className="px-3 py-2 text-sm cursor-pointer hover:bg-[#EEE9F9]"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleDisambiguationSelect(mkt);
                      }}
                    >
                      {mkt}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {showValidationError && validationResult.error && (
            <p className="mt-1 text-sm text-red-600">{validationResult.error}</p>
          )}
        </div>

        <Input
          label="Coverage"
          placeholder="Enter coverage details"
          error={errors.coverage?.message}
          {...register('coverage')}
        />
      </div>

      {(errors.unitProductId || errors.coverage) && (
        <p className="text-sm text-red-600">
          Either Unit/Product ID or Coverage must be provided
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Flight Start Date"
          type="date"
          error={errors.flightStartDate?.message}
          {...register('flightStartDate')}
        />

        <Input
          label="Flight End Date"
          type="date"
          error={errors.flightEndDate?.message}
          {...register('flightEndDate')}
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="isExceptionHold"
          className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
          {...register('isExceptionHold', {
            onChange: (e) => {
              if (e.target.checked && !wasExceptionModalShown()) {
                setShowExceptionModal(true);
                markExceptionModalShown();
              }
            },
          })}
        />
        <label htmlFor="isExceptionHold" className="text-sm font-medium text-neutral-600">
          This is an exception hold (requires special approval)
        </label>
      </div>

      {/* Exception-specific fields */}
      {isExceptionHold && (
        <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-1">
              Hold Expiration Date (override)
            </label>
            <input
              type="date"
              className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              {...register('holdExpirationDate')}
            />
            <p className="mt-1 text-xs text-neutral-400">
              Leave blank to use the default ({formattedDefaultExpiration})
            </p>
          </div>
        </div>
      )}

      <Select
        label="Showing Type"
        options={showingTypes.length > 0
          ? showingTypes.map((v) => ({ value: v.code, label: v.label }))
          : []
        }
        placeholder={isLoadingShowingTypes ? 'Loading...' : showingTypes.length === 0 ? 'None available' : undefined}
        disabled={isLoadingShowingTypes || showingTypes.length === 0}
        error={undefined}
        {...register('showingType')}
      />

      <Select
        label="Service AE (Optional)"
        placeholder={isLoadingServiceAes ? 'Loading...' : 'Select Service AE for cross-region coordination'}
        options={serviceAeOptions}
        error={errors.serviceAeId?.message}
        disabled={isLoadingServiceAes}
        {...register('serviceAeId')}
      />
      <p className="text-xs text-neutral-400 -mt-2">
        Assign a Service AE if this line item requires cross-region coordination
      </p>

      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isLoading}>
          {defaultValues ? 'Update Line Item' : 'Add Line Item'}
        </Button>
      </div>

      <ExceptionModal
        isOpen={showExceptionModal}
        onClose={() => setShowExceptionModal(false)}
      />
    </form>
  );
}

